/* eslint-disable max-len */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { useSelector } from "react-redux";
import { ReactSVG } from "react-svg";
import Loading from "react-fullscreen-loading";

// region Imports - Assets
import LogoFleet from "@assets/FleetLogo/fleetLogo.svg";
import IconObra from "@assets/stateIcons/Na obra.svg";
import { ReactComponent as ManutencaoIcon } from "@assets/icons/maintenance-vehicle.svg";
import { ReactComponent as AlertIconRed } from "@assets/icons/alerts/create-alert-speed-red.svg";
import { ReactComponent as AlertIconGreen } from "@assets/icons/alerts/create-alert-speed-green.svg";
import { ReactComponent as UnblockVehicleIcon } from "@assets/icons/unblocked-vehicle.svg";
import { ReactComponent as BlockVehicleIcon } from "@assets/icons/blocked-vehicle.svg";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
// endregion Imports - Assets
// region Imports - Utility
import $ from "jquery";
import _ from "lodash";
import { computeDistanceBetween, LatLng } from "@utils/geometry";
import { hasValidCoordinates } from "@utils/coordinates";
import utils from "@utils/useful-functions";
// endregion Imports - Utility
// region Imports - Material-UI
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid,
  IconButton,
  Slider,
  Tooltip,
  Typography
} from "@material-ui/core";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
// endregion Imports - Material-UI
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import {
  GlobalMessages,
  MapMessages,
  TimelineVehicleStatesMessages, TravelAlertFullSpeedMessage,
  VehicleAndTravelDetailsMessages, VehicleMaintenanceMessages
} from "@shared/languages/interfaces";
// region Imports - Constants and Enums
import { MapColors, MapConfig } from "@shared/constants/map.enum";
import { FALLBACK_CONSTRUCTION_RADIUS_METERS } from "@shared/constants/construction.constants";
import { ParamsNames } from "@shared/constants/params-names.enum";
import { VehicleStatesTypesID } from "@shared/constants/vehicle-states-types.enum";
import { AlertTypesID, NonAlertEventTypesID } from "@shared/constants/alert-types.enum";
import { EventTypes } from "@shared/constants/event-types.enum";
import { CircleOptions, MarkerOptions, PolygonOptions, PolylineOptions } from "@shared/interfaces/map.interface";
import { Travel } from "@shared/interfaces/travel.interface";
import { TravelAnalytics } from "@shared/interfaces/travel-analytics.interface";
import { Vehicle } from "@shared/interfaces/vehicle.interface";
import { VehicleTypes, VehicleTypesID } from "@shared/constants/vehicle-types.enum";
import { Duration } from "@shared/interfaces/duration.interface";
// endregion Imports - Constants and Enums
// region Imports - Hooks
import { useAuth } from "@hooks/useAuth";
import { useParams } from "@hooks/useParams";
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Store
import { ScreenSize } from "@store/ducks/Screen/screen.type";
// endregion Imports - Store
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Components
import AlertModalList, { formatJustifiedNote } from "@components/Alert/AlertModalList";
import { MapAnalytics, PopperMarker } from "@components/Map";
import TimeLineVehicleStates from "@components/TimeLines/TimeLineVehicleStates";
import TravelData from "@components/TravelData";
import AggregateTrucksTravelData from "@components/AggregateTrucksTravelData";
import VehicleStatus from "@components/VehicleStatus";
import ModalVehicleBlock from "@components/Modal/VehicleBlock";
import ModalVehicleMaintenance from "@components/Modal/VehicleMaintenance";
import WaterAdditionModalList from "@components/WaterAddition/WaterAdditionModalList";

import ModalSpeedTravel from "@components/Modal/SpeedTravelAlert";
// endregion Imports - Components
// region Imports - Shared
import { TLanguages } from "@shared/languages/languageData";
import { Zones } from "@shared/entities/reg_zones.entity";
import { lineStringToLatLngs } from "@shared/utils/geometry/utils";
import { GenericVehicle } from "@shared/interfaces/socket-dto.interface";
import { isVehicleAlertSummary } from "@shared/utils/useful-functions";
import { Alert, Alert as AlertInterface } from "@shared/interfaces/alert.interface";
// endregion Imports - Shared
// region Imports - Styles
import { createGlobalStyle } from "styled-components";
import { ModalVehicleAndTravelDetailsStylesheet as Stylesheet } from "./style";
// endregion Imports - Styles

// region Interfaces
interface Coord {
  lat: number,
  lng: number
}

interface PolylineCoord {
  going: Coord[],
  returning: Coord[],
  zmrc: Coord[]
}

interface MapFilter {
  [key: string]: {
    class: string,
    title: string,
    status: boolean,
    color: string
  }
}

// endregion Interfaces
// region External Functions

// Route colors of this map: the return leg is violet so it is not confused with the construction (yellow)
const ROUTE_COLORS = {
  going: { line: MapColors.GOING, point: "#2E9E63" },
  returning: { line: "#A78BE8", point: "#7046C8" }
};
const ALERT_COLOR = "#D32F2F";
const EVENT_COLOR = "#607D8B";
const DISCHARGE_COLORS = { inConstruction: "#2E7D32", outside: ALERT_COLOR };
const INVOICE_ADDRESS_COLOR = "#F57C00";
const PLANT_COLOR = "#00C853";

/** Plants of a travel (origin and end), once each — the same plant at both ends is drawn and named once
 * @param travel Target travel
 */
const travelPlants = (travel: Travel) => _.uniqBy(
  [travel?.origin, travel?.end].filter((location) => !!location?.latitude && !!location?.longitude),
  (location) => location.id_location ?? `${location.latitude};${location.longitude}`
);

/** A filled circle with a white border, as an SVG data URI (route points and discharge points)
 * @param color Fill color
 * @param size Diameter in pixels
 */
const dotIconUrl = (color: string, size: number): string => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">`
  + `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 1.5}" fill="${color}" stroke="#ffffff" stroke-width="2"/></svg>`
)}`;

/** Route point icon: a small direction arrow rotated to the course, as an SVG data URI (no number: the route
 * only shows where the truck went; the numbers are kept for what happened along it)
 * @param color Color of the leg (going or returning)
 * @param course Course in degrees (0 = north)
 */
const routePointIcon = (color: string, course: number): google.maps.Icon => {
  const svg = "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"18\" height=\"18\">"
    + `<circle cx="9" cy="9" r="7.5" fill="${color}" stroke="#ffffff" stroke-width="1.5"/>`
    + `<path d="M9 4 L12.6 12.2 L9 10.2 L5.4 12.2 Z" fill="#ffffff" transform="rotate(${Number.isFinite(course) ? course : 0} 9 9)"/></svg>`;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: { width: 18, height: 18 } as google.maps.Size,
    anchor: { x: 9, y: 9 } as google.maps.Point
  };
};

/** Numbered marker of something that happened in the travel (state, alert or event), in timeline order
 * @param color Color of the occurrence
 * @param order Number shown on the marker and in the timeline
 */
const keyPointIcon = (color: string, order: number): google.maps.Icon => {
  const text = isLightColor(color) ? "#212121" : "#ffffff";
  const svg = "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"26\" height=\"26\">"
    + `<circle cx="13" cy="13" r="11.5" fill="${color}" stroke="#ffffff" stroke-width="2"/>`
    + "<text x=\"13\" y=\"17.2\" text-anchor=\"middle\" font-family=\"Roboto,Arial,sans-serif\" font-size=\"12\" font-weight=\"700\""
    + ` fill="${text}">${order}</text></svg>`;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: { width: 26, height: 26 } as google.maps.Size,
    anchor: { x: 13, y: 13 } as google.maps.Point
  };
};

/** Whether white text would be hard to read on this color (e.g. the yellow of "Em obra")
 * @param hex Color as #RRGGBB
 */
const isLightColor = (hex: string): boolean => {
  const value = Number.parseInt(hex.replace("#", "").slice(0, 6), 16);
  const [r, g, b] = [(value >> 16) & 255, (value >> 8) & 255, value & 255];

  return 0.299 * r + 0.587 * g + 0.114 * b > 170;
};

/** One line of the travel timeline: what happened, when, and the details that matter */
interface TimelineEntry {
  /** Number shared with the map marker; 0 for an event, which stays outside the numbering */
  order: number;
  /** When it happened (ms), to move the replay there */
  at?: number;
  /** Set when its alert was already handled: what the check mark tells on hover */
  justified?: string;
  color: string;
  when: string;
  title: string;
  detail: string;
}

type TravelMarkerOptions = MarkerOptions & { timeline?: TimelineEntry };

/** A place the truck went through, in time order (GPS jitter while standing still is folded into one point) */
interface RoutePoint {
  position: Coord;
  /** When the truck got here (ms) */
  time: number;
  /** When it left, after a stop (ms); the same as `time` when it just passed by */
  until: number;
  leg: "going" | "returning";
  /** Index of this point in its leg's line (the return line starts at the last going point) */
  pathIndex: number;
  speed?: number;
}

/** The travel as recorded: its route points, its state changes and the span the replay plays */
interface TravelRoute {
  points: RoutePoint[];
  states: { at: number, id: string, description: string, color: string }[];
  start: number;
  end: number;
}

/** Where the replay is at a given moment */
interface ReplayCursor {
  time: number;
  position: Coord;
  leg: RoutePoint["leg"];
  pathIndex: number;
  fraction: number;
  speed?: number;
  state?: TravelRoute["states"][number];
}

// GPS points closer than this to the previous one are the truck standing still (jitter), not a move
const ROUTE_STILL_METERS = 25;
// The replay plays the whole travel in this many seconds at 1x
const REPLAY_BASE_SECONDS = 60;
const REPLAY_TICK_MS = 50;
const REPLAY_RATES = [0.25, 0.5, 1, 2, 4, 8];
const REPLAY_RATE_LABELS: Record<number, string> = { 0.25: "¼×", 0.5: "½×" };
const REPLAY_DEFAULT_RATE = 0.25;
// Remembers that the travel map's full screen was already used (per browser)
const FULLSCREEN_SEEN_KEY = "@Fleet:travelMapFullscreenSeen";
// A right click this close to the route offers the replay from there
const ROUTE_PICK_METERS = 150;
// Passing the same spot again after this long is another pass (the way back, another lap)
const ROUTE_PASS_GAP_MS = 5 * 60 * 1000;

// Route points are moved onto the road-snapped line of their leg (constant: the map re-renders on identity changes)
const ROUTE_MARKER_CLASSES = ["going", "returning"];

/** Distance between two coordinates, as "418 m" or "2,1 km"
 * @param from First point
 * @param to Second point
 */
const formatDistance = (from: Coord, to: Coord): string => {
  const meters = computeDistanceBetween(new LatLng(from.lat, from.lng), new LatLng(to.lat, to.lng));

  return meters >= 1000 ? `${(meters / 1000).toFixed(1).replace(".", ",")} km` : `${Math.round(meters)} m`;
};

/** Generate an array of MarkerOptions (used by map component to create markers)
 * @param travelDetails Target travel to be analyzed
 * @param travelAnalyticRecords Travel analytic records
 * @param vehicle Vehicle object to get Vehicle States
 * @param t Used to translate the marker titles
 * @return A array of MarkerOptions
 */
const generateMarkerOptions = (
  travelDetails: Travel, travelAnalyticRecords: TravelAnalytics[], vehicle: Vehicle, t: (key: string) => string
): TravelMarkerOptions[] => {
  // Minimum distance to consider a point as a new marker
  const MIN_DISTANCE = 120;

  // Array of markers options
  const generatedMarkers: TravelMarkerOptions[] = [];

  // Drop records with corrupted/non-numeric coordinates ("S/N" from truncated tracker
  // packets) so building markers never reaches new LatLng(NaN) and crashes the view
  const validRecords = travelAnalyticRecords.filter(hasValidCoordinates);

  const destination = travelDetails?.destination;

  // States, alerts and events are numbered in the order they happened; the timeline uses the same numbers
  let keyOrder = 0;
  const when = (date?: string | Date) => `${utils.formatDateIfHave(date, "fullDateWithoutYear")}`.slice(0, -3);
  // Duration of what happened; an instant (start = end) has none
  const lasted = (start?: string | Date, end?: string | Date) => (start && end
    && new Date(end).getTime() - new Date(start).getTime() >= 1000
    ? `${utils.formatDateIfHave(utils.calcDataRange(new Date(start), new Date(end)), "durationDescriptiveTime")}`
    : "");
  const awayFromConstruction = (lat: number, lng: number) => (destination?.latitude && destination?.longitude
    && Number.isFinite(lat) && Number.isFinite(lng)
    ? `${formatDistance({ lat, lng }, { lat: Number(destination.latitude), lng: Number(destination.longitude) })} ${t(MapMessages.fromConstruction)}`
    : "");
  const keyMarker = (
    color: string, title: string, start: string | Date | undefined, details: string[], numbered = true, justified?: string
  ): Partial<TravelMarkerOptions> => {
    const detail = details.filter(Boolean).join(" · ");

    // Events are informational and off by default: a plain dot outside the numbering, so hiding them leaves no gaps
    if (!numbered) {
      return {
        icon: { url: dotIconUrl(color, 16), scaledSize: { width: 16, height: 16 } as google.maps.Size, anchor: { x: 8, y: 8 } as google.maps.Point },
        zIndex: 400,
        infoTitle: title,
        timeline: { order: 0, color, when: when(start), at: start ? new Date(start).getTime() : undefined, title, detail, justified }
      };
    }

    keyOrder += 1;

    return {
      icon: keyPointIcon(color, keyOrder),
      zIndex: 500 + keyOrder,
      id: `key-${keyOrder}`,
      infoTitle: `${keyOrder} · ${title}`,
      timeline: { order: keyOrder, color, when: when(start), at: start ? new Date(start).getTime() : undefined, title, detail, justified }
    };
  };

  if (validRecords.length > 0) {
    // Control variable to determine if it is going or returning
    let isGoing = true;

    // Used to compare distance between last point
    let lastMarker: TravelAnalytics = validRecords[0];

    // Create index based on inserted markers
    let index = 0;

    // Count used to add 1 marker and skip others
    let count = 0;

    // Iterate over all travel analytic records
    for (let i = 0; i < validRecords.length; i++) {

      // Get current travel analytic record
      const rec = validRecords[i];

      // Increment index in every loop
      index += 1;

      // Get alert data from vehicle.alerts and merge with rec.alert
      if (rec?.alert) {
        for (let x = 0; x < (travelDetails?.vehicle?.alerts ?? []).length; x++) {
          if (travelDetails?.vehicle?.alerts[x].id_alert === rec.alert.id_alert) {
            rec.alert = { ...rec.alert, ...travelDetails?.vehicle?.alerts[x] };
            break;
          }
        }
      }

      const routeColors = isGoing ? ROUTE_COLORS.going : ROUTE_COLORS.returning;
      const generateTitle = (idx: number, description = "") => `[${idx}] ${description}\n${utils.formatDateIfHave(
        rec.registration_date, "fullDate"
      )}`;
      // The card ends with "play from here": the travel map reads the moment from data-replay-at
      const replayAt = new Date(rec.occurrence_date).getTime();
      const generateInfoContent = (idx: number, going = false) => ReactDOMServer.renderToString(
        <>
          <PopperMarker
            index={idx}
            isGoing={going}
            record={rec}
            destination={destination}
            hideTitle
          />
          {Number.isFinite(replayAt) && (
            <button type="button" className="replay-from" data-replay-at={replayAt}>
              {`▶ ${t(MapMessages.replayFromHere)}`}
            </button>
          )}
        </>
      );

      // Default option: a route point, an arrow pointing where the truck was heading
      const defaultOptions: TravelMarkerOptions = {
        position: {
          lat: Number.parseFloat(rec?.content?.LAT?.content),
          lng: Number.parseFloat(rec?.content?.LON?.content)
        },
        title: generateTitle(index),
        opacity: 1,
        icon: routePointIcon(routeColors.point, Number.parseFloat(rec?.content?.CRS?.content ?? "0")),
        visible: false,
        infoContent: generateInfoContent(index, isGoing),
        infoTitle: `${t(isGoing ? MapMessages.outwardTrip : MapMessages.returnTrip)} · ${when(rec.occurrence_date)}`,
        infoColor: routeColors.point,
        class: isGoing ? "going" : "returning"
      };

      if (validRecords.length - 1 === i) {

        // The last record can also be the travel's last state (e.g. back at the plant): it is numbered too
        if (rec.vehicleState) {
          const stateColor = utils.getStateColor(rec.vehicleState.status.id_vehicle_state_type);

          generatedMarkers.push({
            ...defaultOptions,
            title: generateTitle(index, rec.vehicleState.status.description),
            opacity: 1,
            infoContent: generateInfoContent(index, isGoing),
            infoColor: stateColor,
            infoTag: t(MapMessages.stateTag),
            class: "states",
            ...keyMarker(stateColor, rec.vehicleState.status.description, rec.vehicleState.registration_date,
              [lasted(rec.vehicleState.registration_date, rec.vehicleState.finish_date)])
          });
        }

        // Add vehicle icon in last (current) point of same
        generatedMarkers.push({
          ...defaultOptions,
          title: generateTitle(index, vehicle?.type?.description),
          animation: 1,
          opacity: 1,
          icon: {
            url: utils.getMarkerAccordingVehicleType(
              vehicle,
              vehicle?.states[vehicle?.states?.length - 1]?.status?.id_vehicle_state_type,
              {
                licensePlate: false,
                name: false
              }
            ),
            scaledSize: { width: 30, height: 45 } as google.maps.Size
          },
          infoContent: generateInfoContent(index, isGoing),
          infoTitle: `${vehicle?.code ?? ""} · ${when(rec.occurrence_date)}`,
          class: "last_position"
        });

        lastMarker = rec;

      } else if (rec.vehicleState) {

        // If State is Retornando, then change travel to Return
        if (VehicleStatesTypesID.RETORNANDO === rec.vehicleState.status.id_vehicle_state_type) {
          isGoing = false;
        }

        // States Marker
        const stateColor = utils.getStateColor(rec.vehicleState.status.id_vehicle_state_type);

        generatedMarkers.push({
          ...defaultOptions,
          title: generateTitle(index, rec.vehicleState.status.description),
          opacity: 1,
          infoContent: generateInfoContent(index, isGoing),
          infoColor: stateColor,
          infoTag: t(MapMessages.stateTag),
          class: "states",
          ...keyMarker(stateColor, rec.vehicleState.status.description, rec.vehicleState.registration_date,
            [lasted(rec.vehicleState.registration_date, rec.vehicleState.finish_date)])
        });

        lastMarker = rec;
      } else if (rec.alert && !NonAlertEventTypesID.includes(rec.alert.type?.id_alert_type ?? "")) {

        // Alert Marker (an alert and the event that raised it are the same occurrence: one marker)
        const alertEvent = rec.alert.event ?? rec.event;
        const handledAlert = rec.alert.confirmation_date ? rec.alert : vehicle?.alerts?.find((alert) => alert.id_alert === rec.alert?.id_alert);
        const alertJustified = handledAlert?.confirmation_date ? formatJustifiedNote(handledAlert, t) : undefined;

        generatedMarkers.push({
          ...defaultOptions,
          title: generateTitle(index, rec.alert.type.description),
          opacity: 1,
          infoContent: generateInfoContent(index, isGoing),
          infoColor: ALERT_COLOR,
          infoTag: t(MapMessages.alertTag),
          class: "alerts",
          ...keyMarker(ALERT_COLOR, rec.alert.type.description, rec.alert.occurrence_date, [
            lasted(alertEvent?.start_date ?? rec.alert.occurrence_date, alertEvent?.finish_date),
            awayFromConstruction(Number(alertEvent?.latitude ?? rec.content?.LAT?.content), Number(alertEvent?.longitude ?? rec.content?.LON?.content))
          ], true, alertJustified)
        });

        lastMarker = rec;
      } else if (rec.event) {

        // An event whose alert was already handled (justified) still shows here, with the check mark
        const eventAlert = (rec.event as any)?.alert as AlertInterface | undefined;
        const justified = eventAlert?.confirmation_date ? formatJustifiedNote(eventAlert, t) : undefined;
        const markerOptions: TravelMarkerOptions = {
          ...defaultOptions,
          title: generateTitle(index, rec.event.type.description),
          opacity: 1,
          infoContent: generateInfoContent(index, isGoing),
          infoColor: EVENT_COLOR,
          infoTag: t(MapMessages.eventTag),
          class: "events",
          ...keyMarker(EVENT_COLOR, rec.event.type.description, rec.event.start_date, [
            lasted(rec.event.start_date, rec.event.finish_date),
            awayFromConstruction(Number(rec.event.latitude), Number(rec.event.longitude))
          ], false, justified)
        };

        // Get coords of events when have
        if (rec.event.latitude && rec.event.longitude) {
          markerOptions.position = { lat: Number(rec.event.latitude), lng: Number(rec.event.longitude) };
        }

        // Event Marker
        generatedMarkers.push(markerOptions);

        lastMarker = rec;
      } else if (rec.content?.LAT?.content && rec.content?.LON?.content
        && lastMarker.content?.LAT?.content && lastMarker.content?.LON?.content) {

        const distance = computeDistanceBetween(
          new LatLng(
            Number.parseFloat(rec.content.LAT.content),
            Number.parseFloat(rec.content.LON.content)
          ),
          new LatLng(
            Number.parseFloat(lastMarker.content?.LAT?.content),
            Number.parseFloat(lastMarker.content?.LON?.content)
          )
        );

        if (distance > MIN_DISTANCE && count < 1) {
          lastMarker = rec;
          count += 1;
        } else {
          defaultOptions.visible = false;
          defaultOptions.class = "invisible";

          // To reduce number of markers add 1 skip 1
          if (count === 1) count = 0;
          // Need decrement index because marker will not be added
          index -= 1;
        }

        generatedMarkers.push(defaultOptions);
      } else {

        // Default marker (First)
        generatedMarkers.push(defaultOptions);

        lastMarker = rec;
      }
    }
  }

  // Discharge points recorded for this travel: a small dot, green at the construction, red when the
  // same spot raised an improper discharge — never a circle the size of the construction radius,
  // which made an improper discharge look like it happened inside a construction
  const improperPoints = validRecords
    .filter((rec) => rec.alert?.type?.id_alert_type === AlertTypesID.DESCARREGAMENTO_INDEVIDO
      || rec.event?.type?.description === EventTypes.DESCARREGAMENTO_INDEVIDO)
    .map((rec) => ({
      lat: Number(rec.event?.latitude ?? rec.content?.LAT?.content),
      lng: Number(rec.event?.longitude ?? rec.content?.LON?.content)
    }))
    .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));

  (travelDetails?.coords ?? []).forEach((coord) => {

    const position = { lat: Number.parseFloat(coord.latitude), lng: Number.parseFloat(coord.longitude) };

    if (!Number.isFinite(position.lat) || !Number.isFinite(position.lng)) return;

    const isImproper = improperPoints.some((point) => computeDistanceBetween(
      new LatLng(point.lat, point.lng), new LatLng(position.lat, position.lng)
    ) <= 150);
    const title = t(isImproper ? MapMessages.dischargeOutsideConstruction : MapMessages.dischargeInConstruction);
    const rows = [
      { title: t(GlobalMessages.start), content: utils.formatDateIfHave(coord.occurrence_date ?? coord.registration_date, "fullDate") },
      ...(destination?.latitude && destination?.longitude
        ? [{
          title: t(MapMessages.distanceToConstruction),
          content: formatDistance(position, { lat: Number(destination.latitude), lng: Number(destination.longitude) })
        }]
        : [])
    ];

    generatedMarkers.push({
      position,
      title,
      opacity: 1,
      zIndex: 1000,
      icon: {
        url: dotIconUrl(isImproper ? DISCHARGE_COLORS.outside : DISCHARGE_COLORS.inConstruction, 16),
        scaledSize: { width: 16, height: 16 } as google.maps.Size,
        anchor: { x: 8, y: 8 } as google.maps.Point
      },
      infoContent: ReactDOMServer.renderToString(
        <div style={{ minWidth: 220, font: "13px Roboto, Helvetica, Arial, sans-serif", color: "#424242" }}>
          {rows.map((row) => (
            <div key={row.title} style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "2px 0" }}>
              <span style={{ color: "#757575" }}>{row.title}</span>
              <span style={{ textAlign: "right" }}>{row.content}</span>
            </div>
          ))}
        </div>
      ),
      infoTitle: title,
      infoColor: isImproper ? DISCHARGE_COLORS.outside : DISCHARGE_COLORS.inConstruction,
      class: "discharges"
    });
  });

  return generatedMarkers;
};

/** The travel's route from its records: the recorded positions in time order, split in going and returning legs
 * (from "Retornando" on), plus every state change. Points closer than ROUTE_STILL_METERS to the previous one are
 * the truck standing still and only extend that stop
 * @param records Travel analytic records, in time order
 */
const buildTravelRoute = (records: TravelAnalytics[]): TravelRoute => {
  const points: RoutePoint[] = [];
  const states: TravelRoute["states"] = [];
  const legLength = { going: 0, returning: 0 };
  let leg: RoutePoint["leg"] = "going";

  records.filter(hasValidCoordinates).forEach((rec) => {
    const time = new Date(rec.occurrence_date).getTime();

    if (!Number.isFinite(time)) return;

    if (rec.vehicleState) {
      const id = rec.vehicleState.status.id_vehicle_state_type;

      states.push({
        at: new Date(rec.vehicleState.registration_date ?? rec.occurrence_date).getTime(),
        id,
        description: rec.vehicleState.status.description,
        color: utils.getStateColor(id)
      });
      if (id === VehicleStatesTypesID.RETORNANDO) leg = "returning";
    }

    const position = { lat: Number.parseFloat(rec.content.LAT.content), lng: Number.parseFloat(rec.content.LON.content) };
    const last = points[points.length - 1];

    // Still at the same spot: the stop only gets longer
    if (last && last.leg === leg
      && computeDistanceBetween(new LatLng(last.position.lat, last.position.lng), new LatLng(position.lat, position.lng)) < ROUTE_STILL_METERS) {
      last.until = Math.max(last.until, time);

      return;
    }

    points.push({
      position,
      time,
      until: time,
      leg,
      pathIndex: legLength[leg] + (leg === "returning" && legLength.going > 0 ? 1 : 0),
      speed: rec.content?.SPD?.content !== undefined ? Number(rec.content.SPD.content) : undefined
    });
    legLength[leg] += 1;
  });

  // The replay starts when the truck leaves (not during the wait at the plant) and ends when it is back
  const departure = states.find((state) => state.id === VehicleStatesTypesID.A_CAMINHO)?.at ?? points[0]?.until ?? 0;
  const end = points[points.length - 1]?.time ?? 0;

  return { points, states, start: Math.min(Math.max(departure, points[0]?.time ?? 0), end), end };
};

/** Where the truck was at a given moment: at a point while it stood there, otherwise on the way to the next one
 * @param route The travel route
 * @param time The moment (ms)
 */
const replayCursorAt = (route: TravelRoute, time: number): ReplayCursor | null => {
  const { points, states } = route;

  if (!points.length) return null;

  let reached = 0;

  while (reached + 1 < points.length && points[reached + 1].time <= time) reached += 1;

  const point = points[reached];
  const next = points[reached + 1];
  const state = [...states].reverse().find((change) => change.at <= time) ?? states[0];

  if (!next || time <= point.until) {
    return { time, position: point.position, leg: point.leg, pathIndex: point.pathIndex, fraction: 0, speed: 0, state };
  }

  const fraction = Math.min(1, (time - point.until) / Math.max(1, next.time - point.until));
  const position = {
    lat: point.position.lat + (next.position.lat - point.position.lat) * fraction,
    lng: point.position.lng + (next.position.lng - point.position.lng) * fraction
  };

  // The way from the last going point to the first returning one is the start of the return line
  return {
    time, position, leg: next.leg, pathIndex: next.leg !== point.leg ? 0 : point.pathIndex, fraction, speed: next.speed, state
  };
};

/** Generate an array of PolylinesOptions (used by map component to trace routes lines)
 * @param route Route points of the travel
 */
const generatePolylinesOptions = (route: RoutePoint[]) => {

  // Array of polylines options
  const generatedPolylines: PolylineOptions[] = [];
  const coords: PolylineCoord = { going: [], returning: [], zmrc: [] };

  // Only the recorded positions, in time order: an event's own position (where it started) would pull the line
  // back and the road snapping would draw that back-and-forth along the street
  route.forEach((point) => coords[point.leg].push(point.position));

  // Add marker 'Retornando' in 'going' last position, to create a continuous line
  if (coords["returning"]?.length > 0 && coords["going"].length > 0) {
    coords["returning"].unshift(coords["going"][coords["going"].length - 1]);
  }

  // Default options of polylines, with white chevrons along the line pointing the direction of travel
  const defaultOptions: PolylineOptions = {
    strokeOpacity: 0.8,
    strokeWeight: 8,
    geodesic: true,
    icons: [{
      icon: { path: "M -2,1.5 0,-1.5 2,1.5", strokeColor: "#FFFFFF", strokeOpacity: 1, strokeWeight: 2, scale: 1.5 },
      offset: "40px",
      repeat: "90px"
    }]
  };

  Object.keys(coords).forEach((key) => {
    generatedPolylines.push({
      ...defaultOptions,
      path: coords[key],
      strokeColor: key === "going" ? ROUTE_COLORS.going.line : ROUTE_COLORS.returning.line,
      class: key
    });
  });

  return generatedPolylines;
};

/** Generate an array of CircleOptions (used by map component to create circles)
 * @param travel Target travel to be analyzed
 * @param clientDefaultRadiusMeters Client's "default_construction_radius" param, when loaded
 * @return A array of CircleOptions
 */
const generateCirclesOptions = (travel: Travel, clientDefaultRadiusMeters: number | null): CircleOptions[] => {
  const circles: CircleOptions[] = [];

  if (travel) {

    // When the construction is defined by a drawn area, its radius no longer means anything — the
    // area is plotted by generatePolygonsOptions and every circle sized by that radius is dropped
    const constructionHasArea = !!travel?.destination?.polygon_area;

    // Same precedence the worker applies to decide "Em obra": the construction's own radius, then
    // the client param, then the system fallback. Drawing a fixed 100 m instead put the truck
    // outside a circle the state machine considered it inside, which reads as a bug on the map.
    const effectiveRadiusMeters = Number.parseFloat(`${travel?.destination?.radius}`)
      || clientDefaultRadiusMeters
      || FALLBACK_CONSTRUCTION_RADIUS_METERS;

    // Create 'Obra' circle radius
    if (travel?.destination && !constructionHasArea) {

      // Create NF address circle radius
      if (travel?.destination?.source_latitude && travel?.destination?.source_longitude) {

        // Hollow ring: it is where the delivery ticket says the job site is, not an area the truck must enter
        circles.push({
          strokeColor: INVOICE_ADDRESS_COLOR,
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: INVOICE_ADDRESS_COLOR,
          fillOpacity: 0,
          center: {
            lat: Number.parseFloat(travel?.destination?.source_latitude),
            lng: Number.parseFloat(travel?.destination?.source_longitude)
          },
          radius: effectiveRadiusMeters
        });
      }

      // Create Destination circle — a consolidated construction is drawn solid because its position
      // is locked; one still following the discharges is drawn faded, to show it may still move
      if (travel?.destination?.latitude && travel?.destination?.longitude) {

        circles.push({
          strokeColor: utils.getStateColor(VehicleStatesTypesID.EM_OBRA),
          strokeOpacity: travel?.destination?.consolidated ? 1 : 0.45,
          strokeWeight: travel?.destination?.consolidated ? 3 : 2,
          fillColor: utils.getStateColor(VehicleStatesTypesID.EM_OBRA),
          fillOpacity: travel?.destination?.consolidated ? 0.35 : 0.12,
          center: {
            lat: Number.parseFloat(travel.destination?.latitude),
            lng: Number.parseFloat(travel.destination?.longitude)
          },
          radius: effectiveRadiusMeters
        });
      }
    }

    // The travel's discharge points are drawn as dots by generateMarkerOptions, not as circles

    // Create 'Locais Confiavel' circles
    if (travel?.vehicle?.states) {

      // Filter to get only trusted places and remove duplicates
      const uniqueTrustedPlaces = _.uniqBy([...travel.vehicle.states]
        .filter((tr) => tr.status.id_vehicle_state_type === VehicleStatesTypesID.EM_LOCAL_CONFIAVEL && !tr.location.polygon_area)
        .map((el) => ({
          latitude: el.location.latitude,
          longitude: el.location.longitude,
          radius: el.location.radius
        })), (unq) => `${unq.latitude};${unq.longitude}`);

      // Create and push circle into "circles array"
      uniqueTrustedPlaces.forEach((el) => {
        circles.push({
          strokeColor: utils.getStateColor(VehicleStatesTypesID.EM_LOCAL_CONFIAVEL),
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: utils.getStateColor(VehicleStatesTypesID.EM_LOCAL_CONFIAVEL),
          fillOpacity: 0.35,
          center: { lat: Number.parseFloat(el.latitude), lng: Number.parseFloat(el.longitude) },
          radius: el.radius || MapConfig.RADIUS
        });
      });

    }

    // Plant(s) of the travel — origin and end — in the one color the legend gives to "Usina"
    travelPlants(travel).filter((plant) => !plant.polygon_area).forEach((plant) => {
      circles.push({
        strokeColor: PLANT_COLOR,
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: PLANT_COLOR,
        fillOpacity: 0.3,
        center: { lat: Number.parseFloat(plant.latitude), lng: Number.parseFloat(plant.longitude) },
        radius: Number.parseFloat(`${plant.radius}`) || 80
      });
    });
  }

  return circles;
};

/** A small name tag (white pill with a colored border) as a marker icon, anchored by its bottom center
 * @param text Name shown on the tag
 * @param color Border color, the same as the area it names
 */
const areaLabelIcon = (text: string, color: string): google.maps.Icon => {
  const width = Math.round(16 + text.length * 6.4);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="20">`
    + `<rect x="1" y="1" width="${width - 2}" height="18" rx="9" fill="#ffffff" stroke="${color}" stroke-width="1.5"/>`
    + `<text x="${width / 2}" y="13.5" text-anchor="middle" font-family="Roboto,Arial,sans-serif" font-size="11" font-weight="500"`
    + ` fill="#424242">${text.replace(/&/g, "&amp;").replace(/</g, "&lt;")}</text></svg>`;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: { width, height: 20 } as google.maps.Size,
    anchor: { x: width / 2, y: 22 } as google.maps.Point
  };
};

/** Fixed name tags over each area drawn on the map (construction, delivery-ticket address, plant, trusted
 * locations), so the circles can be told apart without the legend. Always visible and not clickable
 * @param travel Target travel to be analyzed
 * @param clientDefaultRadiusMeters Client's "default_construction_radius" param, when loaded
 * @param t Used to translate the names
 * @return An array of MarkerOptions
 */
const generateAreaLabelOptions = (
  travel: Travel, clientDefaultRadiusMeters: number | null, t: (key: string) => string
): MarkerOptions[] => {
  const labels: MarkerOptions[] = [];

  if (!travel) return labels;

  // Top of a circle (meters to degrees of latitude) or the northernmost vertex of a drawn area
  const top = (latitude: string, longitude: string, radiusMeters: number, polygon?: string | null) => {
    if (polygon) {
      const vertices = lineStringToLatLngs(polygon as any) as google.maps.LatLngLiteral[];

      if (vertices.length) return vertices.reduce((best, point) => (point.lat > best.lat ? point : best));
    }

    return { lat: Number.parseFloat(latitude) + radiusMeters / 111320, lng: Number.parseFloat(longitude) };
  };
  const push = (position: google.maps.LatLngLiteral, text: string, color: string) => {
    if (!Number.isFinite(position.lat) || !Number.isFinite(position.lng)) return;

    labels.push({ position, icon: areaLabelIcon(text, color), clickable: false, zIndex: 1, class: "area_label" });
  };

  const constructionColor = utils.getStateColor(VehicleStatesTypesID.EM_OBRA);
  const destination = travel.destination;

  if (destination?.latitude && destination?.longitude) {
    const radius = Number.parseFloat(`${destination.radius}`) || clientDefaultRadiusMeters || FALLBACK_CONSTRUCTION_RADIUS_METERS;

    push(top(destination.latitude, destination.longitude, radius, destination.polygon_area as any),
      t(destination.consolidated || destination.polygon_area ? MapMessages.construction : MapMessages.constructionNotLocked),
      constructionColor);

    if (destination.source_latitude && destination.source_longitude && !destination.polygon_area) {
      push(top(destination.source_latitude, destination.source_longitude, radius), t(MapMessages.invoiceAddress), INVOICE_ADDRESS_COLOR);
    }
  }

  // Plant: origin and end of the travel (one tag when they are the same place)
  travelPlants(travel).forEach((location) => push(
    top(location.latitude, location.longitude, Number.parseFloat(`${location.radius}`) || 80, location.polygon_area as any),
    t(MapMessages.plant), PLANT_COLOR
  ));

  // Trusted locations the vehicle passed through
  _.uniqBy((travel.vehicle?.states ?? [])
    .filter((state) => state.status.id_vehicle_state_type === VehicleStatesTypesID.EM_LOCAL_CONFIAVEL && state.location)
    .map((state) => state.location), (location) => `${location.latitude};${location.longitude}`)
    .forEach((location) => push(
      top(location.latitude, location.longitude, Number.parseFloat(`${location.radius}`) || MapConfig.RADIUS, location.polygon_area as any),
      t(MapMessages.trustedLocation), "#666666"
    ));

  return labels;
};

/** Generate an array of PolygonOptions (used by map component to create polygons locations)
 * @param travel Target travel to be analyzed
 * @return A array of PolygonOptions
 */
const generatePolygonsOptions = (travel: Travel): PolygonOptions[] => {
  const polygons: PolygonOptions[] = [];

  if (travel) {

    // Create 'Obra' polygon — the construction area replaces its radius when one is drawn. A drawn
    // area always means a consolidated construction, so it gets the same solid treatment
    if (travel?.destination?.polygon_area) {

      polygons.push({
        paths: lineStringToLatLngs(travel.destination.polygon_area),
        strokeColor: utils.getStateColor(VehicleStatesTypesID.EM_OBRA),
        strokeOpacity: 1,
        strokeWeight: 3,
        fillColor: utils.getStateColor(VehicleStatesTypesID.EM_OBRA),
        fillOpacity: 0.35
      });
    }

    // Create 'Locais Confiavel' circles
    if (travel?.vehicle?.states) {

      // Filter to get only trusted places and remove duplicates
      const uniqueTrustedPlaces = _.uniqBy([...travel.vehicle.states]
        .filter((tr) => tr.status.id_vehicle_state_type === VehicleStatesTypesID.EM_LOCAL_CONFIAVEL &&
          tr.location.polygon_area)
        .map((el) => ({
          polygon_area: el.location.polygon_area
        })), (unq) => `${ unq.polygon_area }`);

      // Create and push polygon into "polygons array"
      uniqueTrustedPlaces.forEach((el) => {
        polygons.push({
          paths: lineStringToLatLngs(el.polygon_area!),
          strokeColor: utils.getStateColor(VehicleStatesTypesID.EM_LOCAL_CONFIAVEL),
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: utils.getStateColor(VehicleStatesTypesID.EM_LOCAL_CONFIAVEL),
          fillOpacity: 0.35
        });
      });
    }

    // Plant(s) of the travel drawn as areas — the same color as the plant circles. Origin and end used to be
    // compared by their polygon object, which never matched, so the end plant was drawn blue over the origin
    travelPlants(travel).filter((plant) => !!plant.polygon_area).forEach((plant) => {
      polygons.push({
        paths: lineStringToLatLngs(plant.polygon_area!),
        strokeColor: PLANT_COLOR,
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: PLANT_COLOR,
        fillOpacity: 0.3
      });
    });
  }

  return polygons;
};

/** Generate object of default map filter
 * @param t Used to translate messages
 * @param vehicle Used to analyze if vehicle is "Veiculo de apoio"
 * @return A object of MapFilter
 */
const generateDefaultMapFilter = (t, vehicle?: Vehicle): MapFilter => ({

  going: {
    class: "going",
    title: t(MapMessages.outwardTrip),
    status: vehicle?.type?.id_vehicle_type !== VehicleTypesID.VEICULO_DE_APOIO,
    color: ROUTE_COLORS.going.point
  },
  returning: {
    class: "returning",
    title: t(MapMessages.returnTrip),
    status: vehicle?.type?.id_vehicle_type !== VehicleTypesID.VEICULO_DE_APOIO,
    color: ROUTE_COLORS.returning.point
  },
  states: { class: "states", title: t(MapMessages.states), status: true, color: "#7E73F8" },
  alerts: { class: "alerts", title: t(MapMessages.alerts), status: true, color: ALERT_COLOR },
  events: { class: "events", title: t(MapMessages.events), status: false, color: EVENT_COLOR },
  discharges: { class: "discharges", title: t(MapMessages.discharges), status: true, color: DISCHARGE_COLORS.inConstruction },
  zmrc: { class: "zmrc", title: t(MapMessages.zmrc), status: true, color: MapColors.ZMRC }
});

type TravelTimelineProps = {
  items: (TimelineEntry & { markerIndex: number })[];
  onSelect: (markerIndex: number) => void;
  /** Kept open (full screen, where there is room for it) */
  forceOpen?: boolean;
  /** Where tips are drawn: in full screen only the full screen element is visible */
  tooltipContainer?: HTMLElement | null;
  t: (key: string) => string;
};

/** What happened in the travel, in order, with the same numbers as the map; clicking an item opens it on the map.
 * Collapsed by default: a single line under the map until the order of what happened is needed
 */
const TravelTimeline: React.FC<TravelTimelineProps> = ({ items, onSelect, forceOpen = false, tooltipContainer, t }) => {

  const [open, setOpen] = useState(false);
  const isOpen = open || forceOpen;

  if (!items.length) return null;

  return (
    <div className="vehicle-travel-details__map-timeline">
      <button type="button" className="timeline-toggle" aria-expanded={isOpen} onClick={() => setOpen((state) => !state)}>
        <ExpandMoreIcon className={`timeline-chevron${isOpen ? " is-open" : ""}`} aria-hidden="true" />
        {`${t(MapMessages.timeline)} (${items.length})`}
      </button>
      {isOpen && (
        <ol>
          {items.map((item) => (
            <li key={`timeline-${item.markerIndex}`}>
              <button type="button" onClick={() => onSelect(item.markerIndex)}>
                <span
                  className={`badge${item.order ? "" : " is-dot"}`}
                  style={{ background: item.color, color: isLightColor(item.color) ? "#212121" : "#FFFFFF" }}
                >
                  {item.order || ""}
                </span>
                <span className="when">{item.when}</span>
                <span className="what">
                  <b>{item.title}</b>
                  {item.detail && <span className="detail">{item.detail}</span>}
                </span>
                {item.justified ? (
                  <Tooltip
                    title={item.justified}
                    placement="top"
                    arrow
                    classes={{ popper: "map-legend-tooltip" }}
                    PopperProps={tooltipContainer ? { container: tooltipContainer } : undefined}
                  >
                    <CheckCircleIcon className="justified-check" />
                  </Tooltip>
                ) : <span />}
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

// The global stylesheet pins MUI tooltips at z-index 81, under any dialog: the legend's tips must sit above this one
const MapLegendTooltipStyle = createGlobalStyle`
  .MuiTooltip-popper.map-legend-tooltip {
    z-index: 1500 !important;

    .MuiTooltip-tooltip {
      white-space: pre-line;
    }
  }
`;

// Layers drawn as a line on the map (the others are points)
const LINE_LAYERS = ["going", "returning", "zmrc"];

type MapLegendProps = {
  mapFilter: MapFilter;
  setMapFilter: React.Dispatch<React.SetStateAction<MapFilter>>;
  /** Where tips are drawn: in full screen only the full screen element is visible */
  tooltipContainer?: HTMLElement | null;
  t: (key: string) => string;
};

/** Map legend below the travel map: a toggle per layer (route legs, states, alerts, events, discharges, ZMRC)
 * and what each area drawn on the map means, so the circles and colors can be read without guessing
 */
const MapLegend: React.FC<MapLegendProps> = ({ mapFilter, setMapFilter, tooltipContainer, t }) => {

  const constructionColor = utils.getStateColor(VehicleStatesTypesID.EM_OBRA);
  const areas = [
    { key: "construction", label: MapMessages.construction, help: MapMessages.constructionHelp,
      swatch: <span className="area" style={{ borderColor: constructionColor, background: `${constructionColor}59` }} /> },
    { key: "construction-not-locked", label: MapMessages.constructionNotLocked, help: MapMessages.constructionNotLockedHelp,
      swatch: <span className="area" style={{ borderColor: `${constructionColor}73`, background: `${constructionColor}1F` }} /> },
    { key: "invoice-address", label: MapMessages.invoiceAddress, help: MapMessages.invoiceAddressHelp,
      swatch: <span className="area" style={{ borderColor: INVOICE_ADDRESS_COLOR, background: "transparent" }} /> },
    { key: "plant", label: MapMessages.plant, help: MapMessages.plantHelp,
      swatch: <span className="area" style={{ borderColor: PLANT_COLOR, background: `${PLANT_COLOR}4D` }} /> },
    { key: "trusted-location", label: MapMessages.trustedLocation, help: MapMessages.trustedLocationHelp,
      swatch: <span className="area" style={{ borderColor: "#666666", background: "#66666659" }} /> },
    { key: "discharge-in", label: MapMessages.dischargeInConstruction, help: MapMessages.dischargeInConstructionHelp,
      swatch: <span className="swatch dot" style={{ background: DISCHARGE_COLORS.inConstruction }} /> },
    { key: "discharge-out", label: MapMessages.dischargeOutsideConstruction, help: MapMessages.dischargeOutsideConstructionHelp,
      swatch: <span className="swatch dot" style={{ background: DISCHARGE_COLORS.outside }} /> }
  ];

  return (
    <div className="vehicle-travel-details__map-legend">
      <MapLegendTooltipStyle />
      <div className="legend-row">
        <span className="legend-label">{t(MapMessages.showOnMap)}</span>
        <div className="legend-items">
          {Object.keys(mapFilter).map((key) => {
            const filter = mapFilter[key];

            return (
              <button
                type="button"
                key={`map-layer-${key}`}
                className={`legend-toggle${filter.status ? " is-on" : ""}`}
                aria-pressed={filter.status}
                onClick={() => setMapFilter((state) => ({ ...state, [key]: { ...state[key], status: !state[key].status } }))}
              >
                {key === "discharges" ? (
                  <span className="swatch-pair">
                    <span className="swatch dot" style={{ background: DISCHARGE_COLORS.inConstruction }} />
                    <span className="swatch dot" style={{ background: DISCHARGE_COLORS.outside }} />
                  </span>
                ) : (
                  <span className={`swatch ${LINE_LAYERS.includes(key) ? "line" : "dot"}`} style={{ background: filter.color }} />
                )}
                {filter.title}
              </button>
            );
          })}
        </div>
      </div>
      <div className="legend-row">
        <span className="legend-label">{t(MapMessages.areas)}</span>
        <div className="legend-items">
          {areas.map((area) => (
            <Tooltip
              key={`map-area-${area.key}`}
              title={t(area.help)}
              placement="top"
              arrow
              classes={{ popper: "map-legend-tooltip" }}
              PopperProps={tooltipContainer ? { container: tooltipContainer } : undefined}
            >
              <span className="legend-item" tabIndex={0}>
                {area.swatch}
                {t(area.label)}
                <HelpOutlineIcon className="legend-help" aria-hidden="true" />
              </span>
            </Tooltip>
          ))}
        </div>
      </div>
      <p className="legend-hint">
        {t(MapMessages.legendHint)}
        <br />
        {t(MapMessages.legendOrderHint)}
        <br />
        {t(MapMessages.replayHint)}
      </p>
    </div>
  );
};

/** Distance between two points, in meters */
const metersBetween = (from: Coord, to: Coord): number => computeDistanceBetween(new LatLng(from.lat, from.lng), new LatLng(to.lat, to.lng));

type TravelHudProps = {
  cursor: ReplayCursor;
  route: TravelRoute;
  destination?: Coord;
  /** Speed limit of the travel (or of the vehicle), in km/h */
  maxSpeed?: number;
  vehicleTypeId?: string;
  /** Travel with a delivery note (its obra is known); otherwise an automatic travel */
  hasNote: boolean;
  t: (key: string) => string;
};

/** What the replay shows over the map, like a navigation app: a speedometer against the limit, the time and the
 * state, and the trip from the plant to the obra with the truck on it
 */
const TravelHud: React.FC<TravelHudProps> = ({ cursor, route, destination, maxSpeed, vehicleTypeId, hasNote, t }) => {

  const speed = Math.max(0, Math.round(cursor.speed ?? 0));
  // Travel time counted from the departure ("A caminho")
  const elapsed = `${utils.formatDateIfHave(utils.calcDataRange(new Date(route.start), new Date(Math.max(route.start, cursor.time))),
    "durationDescriptiveTime")}`;
  const isOverLimit = !!maxSpeed && speed > maxSpeed;
  const [day, time] = `${utils.formatDateIfHave(new Date(cursor.time), "fullDateWithoutYear")}`.split(" ");
  // The trip bar needs the travel's obra: the nota's, the one a pump is bound to, or the one an automatic travel
  // recognized (tagged as such); an automatic travel that recognized none only says so
  const isAutomatic = !hasNote && vehicleTypeId !== VehicleTypesID.CAMINHAO_BOMBA;
  const showTrip = !!destination && route.points.length > 0;

  // Where the truck is between the plant (left) and the obra (right): by its distance to the obra, against the
  // distance it started from (going) or the one it ended at (returning)
  let tripAt = 0;
  let toObra = 0;

  if (showTrip && destination) {
    toObra = metersBetween(cursor.position, destination);

    const reference = cursor.leg === "going" ? route.points[0].position : route.points[route.points.length - 1].position;
    const total = metersBetween(reference, destination);
    const share = total > 0 ? Math.min(1, Math.max(0, toObra / total)) : 0;

    // Going it moves from the plant to the obra; returning, from the obra back to the plant
    tripAt = 1 - share;
  }

  const legColor = cursor.leg === "going" ? ROUTE_COLORS.going.point : ROUTE_COLORS.returning.point;

  return (
    <div className="travel-hud">
      <div className="hud-row">
        <div
          className={`hud-speed${isOverLimit ? " is-over" : ""}`}
          role="img"
          aria-label={`${t(GlobalMessages.speed)}: ${speed} km/h${maxSpeed ? ` / ${maxSpeed} km/h` : ""}`}
        >
          <span className="hud-speed__value">{speed}</span>
          <span className="hud-speed__unit">{maxSpeed ? `/ ${maxSpeed} km/h` : "km/h"}</span>
        </div>
        <div className="hud-card">
          <span className="hud-time">
            {time}
            <small>{day}</small>
          </span>
          <span className="hud-elapsed">{`${t(MapMessages.travelTime)}: ${elapsed}`}</span>
          {cursor.state && (
            <span className="hud-state">
              <span className="hud-state-icon" style={{ borderColor: cursor.state.color }}>{utils.getStateIcon(cursor.state.id)}</span>
              {cursor.state.description}
            </span>
          )}
        </div>
      </div>
      {showTrip ? (
        <div className="hud-trip">
          <span className="hud-trip-icon">{utils.getStateIcon(VehicleStatesTypesID.NA_USINA)}</span>
          <span className="hud-track">
            <span
              className="hud-track-done"
              style={cursor.leg === "going"
                ? { left: 0, width: `${tripAt * 100}%`, background: legColor }
                : { left: `${tripAt * 100}%`, right: 0, background: legColor }}
            />
            <span
              className={`hud-truck${cursor.leg === "returning" ? " is-returning" : ""}`}
              style={{ left: `${tripAt * 100}%` }}
            >
              {vehicleTypeId ? utils.getVehicleIconAccordingType(vehicleTypeId) : null}
            </span>
          </span>
          <span className="hud-trip-icon">{utils.getStateIcon(VehicleStatesTypesID.EM_OBRA)}</span>
          <span className="hud-trip-label">
            {`${formatDistance(cursor.position, destination as Coord)} ${t(MapMessages.fromConstruction)} · `
              + `${t(cursor.leg === "going" ? MapMessages.outwardTrip : MapMessages.returnTrip)}`}
            {isAutomatic && <span className="hud-auto-tag">{t(MapMessages.automaticConstruction)}</span>}
          </span>
        </div>
      ) : (
        <div className="hud-trip is-automatic">
          <span className="hud-trip-icon">{vehicleTypeId ? utils.getVehicleIconAccordingType(vehicleTypeId) : null}</span>
          <span className="hud-trip-label">{t(MapMessages.automaticTravel)}</span>
        </div>
      )}
    </div>
  );
};

type TravelMapProps = {
  markers: TravelMarkerOptions[];
  polylines: PolylineOptions[];
  polygons: PolygonOptions[];
  polygonPlaces: PolygonOptions[];
  circles: CircleOptions[];
  route: TravelRoute;
  destination?: Coord;
  mapFilter: MapFilter;
  setMapFilter: React.Dispatch<React.SetStateAction<MapFilter>>;
  /** What the modal shows around the map (header and travel data): shown beside the map in full screen */
  details?: React.ReactNode;
  /** Speed limit shown by the replay's speedometer (km/h) */
  maxSpeed?: number;
  vehicleTypeId?: string;
  /** Travel with a delivery note */
  hasNote: boolean;
  t: (key: string) => string;
};

/** The travel map with its replay, timeline and legend. It keeps the replay clock itself, so playing the route
 * re-renders only this part of the modal. Its full screen takes all of it, plus the travel data
 */
const TravelMap: React.FC<TravelMapProps> = ({
  markers, polylines, polygons, polygonPlaces, circles, route, destination, mapFilter, setMapFilter, details, maxSpeed, vehicleTypeId,
  hasNote, t
}) => {

  const rootRef = useRef<HTMLDivElement>(null);
  const mapAreaRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [focusMarker, setFocusMarker] = useState<{ index: number, nonce: number } | undefined>();
  // Moment shown by the replay (ms); null or the end of the travel shows the route as usual
  const [replayTime, setReplayTime] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(REPLAY_DEFAULT_RATE);
  // Native full screen, or the same layout over the whole window where the browser refuses it
  const [fullscreen, setFullscreen] = useState<"native" | "window" | null>(null);
  // The full screen button pulses until it is used once in this browser
  const [fullscreenSeen, setFullscreenSeen] = useState(() => {
    try {
      return window.localStorage.getItem(FULLSCREEN_SEEN_KEY) === "1";
    } catch (error) {
      return true;
    }
  });
  // Right click on the route: each time the truck went by that spot
  const [routeMenu, setRouteMenu] = useState<{ x: number, y: number, passes: RoutePoint[] } | null>(null);

  const { start, end } = route;
  const canReplay = end > start;
  const isFullscreen = !!fullscreen;
  const tooltipContainer = isFullscreen ? rootRef.current : undefined;

  const filter = useMemo(() => Object.keys(mapFilter).map((key) => ({ class: mapFilter[key].class, status: mapFilter[key].status })), [mapFilter]);

  // Timeline: what happened, in order, pointing at its index in the markers list; only the layers shown on the map
  const timeline = useMemo(() => markers
    .map((marker, markerIndex) => (marker.timeline && mapFilter[marker.class || ""]?.status ? { ...marker.timeline, markerIndex } : null))
    .filter((item): item is TimelineEntry & { markerIndex: number } => !!item), [markers, mapFilter]);

  // Replay clock
  useEffect(() => {
    if (!playing || !canReplay) return undefined;

    const step = ((end - start) / (REPLAY_BASE_SECONDS * 1000)) * playbackRate * REPLAY_TICK_MS;
    const timer = window.setInterval(() => setReplayTime((current) => Math.min(end, (current ?? start) + step)), REPLAY_TICK_MS);

    return () => window.clearInterval(timer);
  }, [playing, playbackRate, start, end, canReplay]);

  // Stop at the end of the travel
  useEffect(() => {
    if (playing && replayTime !== null && replayTime >= end) setPlaying(false);
  }, [playing, replayTime, end]);

  const cursor = useMemo(
    () => (replayTime === null || replayTime >= end ? null : replayCursorAt(route, replayTime)), [route, replayTime, end]
  );
  // Out of a replay the HUD stays on screen showing how the travel ended (its last position and last state)
  const hudCursor = useMemo(() => {
    if (cursor) return cursor;
    if (!canReplay) return null;

    const last = replayCursorAt(route, end);

    return last ? { ...last, state: route.states[route.states.length - 1] ?? last.state } : null;
  }, [cursor, canReplay, route, end]);
  const replay = useMemo(() => (cursor
    ? { classes: ROUTE_MARKER_CLASSES, leg: cursor.leg, pathIndex: cursor.pathIndex, fraction: cursor.fraction, follow: playing }
    : null), [cursor, playing]);

  // Full screen holds the whole travel map (replay, timeline, legend and travel data), not only Google's map
  useEffect(() => {
    const onChange = () => setFullscreen(rootRef.current && document.fullscreenElement === rootRef.current ? "native" : null);

    document.addEventListener("fullscreenchange", onChange);

    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // The route menu closes on a click anywhere else or on Escape
  useEffect(() => {
    if (!routeMenu) return undefined;

    const close = (event: Event) => {
      if (event instanceof KeyboardEvent ? event.key === "Escape" : !menuRef.current?.contains(event.target as Node)) setRouteMenu(null);
    };

    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);

    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [routeMenu]);

  const seek = (time: number) => setReplayTime(Math.min(end - 1, Math.max(start, time)));
  // Listeners bound once reach the current seek through a ref
  const seekRef = useRef(seek);

  // "Play from here": jump there and play
  const playFrom = (time: number) => {
    seek(time);
    setPlaying(true);
  };

  seekRef.current = canReplay ? playFrom : () => undefined;

  // Inside the map: the "play from here" button of a point's card, and no browser menu on right click (ours opens)
  useEffect(() => {
    const area = mapAreaRef.current;

    if (!area) return undefined;

    const onClick = (event: MouseEvent) => {
      const button = (event.target as HTMLElement | null)?.closest?.("[data-replay-at]") as HTMLElement | null;
      const at = Number(button?.dataset.replayAt);

      if (button && Number.isFinite(at)) seekRef.current(at);
    };
    const onContextMenu = (event: MouseEvent) => event.preventDefault();

    // Capture phase: the map stops some events from bubbling out of its cards
    area.addEventListener("click", onClick, true);
    area.addEventListener("contextmenu", onContextMenu, true);

    return () => {
      area.removeEventListener("click", onClick, true);
      area.removeEventListener("contextmenu", onContextMenu, true);
    };
  }, []);

  // Right click near the route: offer the replay from each time the truck went by there
  const openRouteMenu = useCallback((
    { position, clientX, clientY }: { position: google.maps.LatLngLiteral, clientX: number, clientY: number }
  ) => {
    const area = mapAreaRef.current?.getBoundingClientRect();

    if (!area || !canReplay) return;

    const clicked = new LatLng(position.lat, position.lng);
    const passes: { point: RoutePoint, meters: number }[] = [];
    let lastPassed = -Infinity;

    route.points.forEach((point) => {
      const meters = computeDistanceBetween(new LatLng(point.position.lat, point.position.lng), clicked);

      if (meters > ROUTE_PICK_METERS) return;

      if (point.time - lastPassed > ROUTE_PASS_GAP_MS) passes.push({ point, meters });
      else if (meters < passes[passes.length - 1].meters) passes[passes.length - 1] = { point, meters };
      lastPassed = point.until;
    });

    if (!passes.length) {
      setRouteMenu(null);

      return;
    }

    const shown = passes.slice(0, 4);

    setRouteMenu({
      x: Math.max(0, Math.min(clientX - area.left, area.width - 220)),
      y: Math.max(0, Math.min(clientY - area.top, area.height - 44 - shown.length * 34)),
      passes: shown.map(({ point }) => point)
    });
  }, [route, canReplay]);

  const togglePlay = () => {
    if (playing) {
      setPlaying(false);

      return;
    }
    if (replayTime === null || replayTime >= end) setReplayTime(start);
    setPlaying(true);
  };
  const toggleFullscreen = () => {
    if (!fullscreenSeen) {
      setFullscreenSeen(true);
      try {
        window.localStorage.setItem(FULLSCREEN_SEEN_KEY, "1");
      } catch (error) {
        // Storage unavailable (private window): the hint just shows again next time
      }
    }
    if (fullscreen === "native") {
      document.exitFullscreen().catch(() => undefined);

      return;
    }
    if (fullscreen === "window") {
      setFullscreen(null);

      return;
    }

    // Some browsers (and embedded views) refuse, ignore or drop the request: whatever does not end in full screen
    // within a second gets the window layout
    const fallback = () => {
      if (document.fullscreenElement !== rootRef.current) setFullscreen("window");
    };
    const request = rootRef.current?.requestFullscreen?.();

    if (!request) {
      fallback();

      return;
    }
    request.catch(fallback);
    window.setTimeout(fallback, 1000);
  };
  const clock = (time: number) => `${utils.formatDateIfHave(new Date(time), "fullDateWithoutYear")}`;
  const legName = (leg: RoutePoint["leg"]) => t(leg === "going" ? MapMessages.outwardTrip : MapMessages.returnTrip);

  return (
    <div
      ref={rootRef}
      className={`vehicle-travel-details__travel-map${isFullscreen ? " is-fullscreen" : ""}${fullscreen === "window" ? " is-window" : ""}`}
    >
      <div className="travel-map__main">
        <div ref={mapAreaRef} className="vehicle-travel-details__map-area">
          <MapAnalytics
            mapHeight={isFullscreen ? "100%" : 400}
            polygons={polygons}
            polygonPlaces={polygonPlaces}
            markers={markers}
            polylines={polylines}
            circles={circles}
            filter={filter}
            snapToRoads
            snapMarkerClasses={ROUTE_MARKER_CLASSES}
            declutterMarkerClasses={ROUTE_MARKER_CLASSES}
            focusMarker={focusMarker}
            replay={replay}
            onContextMenu={openRouteMenu}
            fullscreenControl={false}
          />
          <Tooltip
            title={t(isFullscreen ? MapMessages.exitFullscreen : MapMessages.fullscreenHint)}
            placement="left"
            arrow
            classes={{ popper: "map-legend-tooltip" }}
            PopperProps={tooltipContainer ? { container: tooltipContainer } : undefined}
          >
            <button
              type="button"
              className={`map-fullscreen${!fullscreenSeen && !isFullscreen ? " is-new" : ""}`}
              aria-label={t(isFullscreen ? MapMessages.exitFullscreen : MapMessages.fullscreen)}
              onClick={toggleFullscreen}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </button>
          </Tooltip>
          {hudCursor && (
            <TravelHud
              cursor={hudCursor}
              route={route}
              destination={destination}
              maxSpeed={maxSpeed}
              vehicleTypeId={vehicleTypeId}
              hasNote={hasNote}
              t={t}
            />
          )}
          {routeMenu && (
            <div ref={menuRef} className="vehicle-travel-details__route-menu" role="menu" style={{ left: routeMenu.x, top: routeMenu.y }}>
              <span className="menu-title">{t(MapMessages.replayFromHere)}</span>
              {routeMenu.passes.map((point) => (
                <button
                  type="button"
                  role="menuitem"
                  key={`route-pass-${point.time}`}
                  onClick={() => {
                    playFrom(point.time);
                    setRouteMenu(null);
                  }}
                >
                  <PlayArrowIcon aria-hidden="true" />
                  {`${clock(point.time).slice(0, -3)} · ${legName(point.leg)}`}
                </button>
              ))}
            </div>
          )}
        </div>
        {canReplay && (
          <div className="vehicle-travel-details__replay">
            <IconButton size="small" aria-label={t(playing ? MapMessages.replayPause : MapMessages.replayPlay)} onClick={togglePlay}>
              {playing ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            <Slider
              className="replay-slider"
              min={start}
              max={end}
              step={1000}
              value={replayTime ?? end}
              onChange={(_event, value) => setReplayTime(value as number)}
              aria-label={t(MapMessages.travelMoment)}
            />
            <span className="replay-clock">{clock(replayTime ?? end).slice(0, -3)}</span>
            <div className="replay-rates" role="group" aria-label={t(MapMessages.playbackSpeed)}>
              {REPLAY_RATES.map((rate) => (
                <button
                  type="button"
                  key={`replay-rate-${rate}`}
                  className={rate === playbackRate ? "is-on" : ""}
                  aria-pressed={rate === playbackRate}
                  onClick={() => setPlaybackRate(rate)}
                >
                  {REPLAY_RATE_LABELS[rate] ?? `${rate}×`}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="travel-map__side">
        {isFullscreen && details && <div className="travel-map__details">{details}</div>}
        <TravelTimeline
          items={timeline}
          forceOpen={isFullscreen}
          tooltipContainer={tooltipContainer}
          onSelect={(index) => {
            // Bring the map back into view: the timeline sits below it, often scrolled past it
            mapAreaRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
            setFocusMarker({ index, nonce: Date.now() });

            // During a replay the clock jumps there too
            const at = markers[index]?.timeline?.at;

            if (cursor && at !== undefined) seek(at);
          }}
          t={t}
        />
        <MapLegend mapFilter={mapFilter} setMapFilter={setMapFilter} tooltipContainer={tooltipContainer} t={t} />
      </div>
    </div>
  );
};

// endregion External Functions

type ModalVehicleAndTravelDetailsProps = {
  vehicle: GenericVehicle;
  travel: Travel;
  open: boolean;
  onClose: () => void;
  historical?: boolean;
  overAll?: boolean;
  onlyView?: boolean;
  isBlocked?: boolean
};
const ModalVehicleAndTravelDetails: React.FC<ModalVehicleAndTravelDetailsProps> = ({
  vehicle: genericVehicle,
  travel,
  open,
  onClose,
  historical = false,
  overAll = false,
  onlyView = false,
  isBlocked = genericVehicle.blocked
}) => {

  // region Hooks
  const { addToast } = useToast();

  const improperDischargeAlerts = useSelector(({ alerts: state }) => state.all.list.filter(
    (alert) => alert.type.id_alert_type === AlertTypesID.DESCARREGAMENTO_INDEVIDO
  ));

  const screen = useSelector(({ screen: state }) => state);

  const { user } = useAuth();

  const { getParamsObj } = useParams();

  const { t, i18n } = useTranslation();
  // endregion Hooks
  // region States
  const [showMap, setShowMap] = useState(false);
  const [openModalVehicleBlock, setOpenModalVehicleBlock] = useState(false);
  const [openModalVehicleMaintenance, setOpenModalVehicleMaintenance] = useState(false);
  const [openModalCreateAlert, setOpenModalCreateAlertSpeed] = useState(false);

  const [travelAnalyticRecords, setTravelAnalyticRecords] = useState<TravelAnalytics[]>([]);
  const [travelDetails, setTravelDetails] = useState<Travel>({} as Travel);

  // Client's default construction radius, used to size the obra circle exactly as the worker does
  const [clientDefaultRadiusMeters, setClientDefaultRadiusMeters] = useState<number | null>(null);

  const [vehicle, setVehicle] = useState<Vehicle>(genericVehicle as Vehicle);

  const [isLoading, setIsLoading] = useState(false);

  const [alerts, setAlerts] = useState<AlertInterface[]>([]);

  const [loadingTravelMap, setLoadingTravelMap] = useState(false);
  const [mapFilter, setMapFilter] = useState(generateDefaultMapFilter(t, vehicle));
  // endregion States
  // region Constants
  const isMobile = screen.size !== ScreenSize.large;

  const isAggregateOrSiloTruck = vehicle.type.description === VehicleTypes.CARRETA_SILO
    || vehicle.type.description === VehicleTypes.CARRETA_AGREGADOS;

  const waterAdditions = travel?.water_additions?.map((waterAddition) => ({
    ...waterAddition,
    // water reassigned from a previous travel is reused in this load (added before this travel's
    // discharge) → counts as PRE-discharge here; it is still shown segmented by origin in the list.
    is_post_discharge: waterAddition?.is_reassigned_post_discharge
      ? false
      : (waterAddition?.start_date && travel?.last_due_unload_date)
        ? waterAddition?.start_date > travel?.last_due_unload_date
        : undefined
  }));
  // endregion Constants
  // region Functions

  const [polygons, setPolygons] = useState<PolygonOptions[]>([]);
  // Marker opened from the timeline (a new nonce reopens the same one)
  const [polylinesZMRC, setPolylinesZMRC] = useState<PolylineOptions[]>([]);

  /** Search details of selected travel to Analyze (just coords)
   * @param travel Target travel to be analyzed
   */
  const analyzeTravelDetail = useCallback(async (travel: Travel) => {

    if (!travel) {
      addToast({
        type: "info",
        title: t(VehicleAndTravelDetailsMessages.noActiveTravel),
        description: t(VehicleAndTravelDetailsMessages.noActiveTravelDescription)
      });
    } else {

      const travelDet: Travel = { ..._.clone(travel), vehicle: { ..._.clone(vehicle) } };
      let alertMessageContent = "";

      // Internal functions
      const FreadTravelAnalytics = async () => (await api.get("travels/get-travel-analytics",
        { params: { idTravel: travel.id_travel, fullAnalytics: false } })).data;
      const FreadTravelCoords = async () => (await api.get(`coords/read/${travel.id_travel}`)).data;

      // Show loading
      setLoadingTravelMap(true);

      // Read Zones
      readZones();

      try {

        const promise1 = FreadTravelAnalytics();
        const promise2 = travelDet?.destination ? await FreadTravelCoords() : await Promise.resolve();

        const [travelAnalyticsResponse, travelCoordsResponse] = await Promise.all([
          promise1, promise2
        ]);

        // Verify if have some alert in analytics data travel response
        if (travelAnalyticsResponse.status === "success") setTravelAnalyticRecords(travelAnalyticsResponse.result);
        else alertMessageContent += travelAnalyticsResponse.message;

        // Verify if we have Location Coords
        if (travelDet?.destination && travelCoordsResponse?.status === "success") {
          travelDet.coords = travelCoordsResponse.result;
          setTravelDetails(travelDet);
        } else alertMessageContent += travelCoordsResponse?.message ? travelCoordsResponse.message : "";

        // If we have some alert, show message to user
        if (!_.isEmpty(alertMessageContent)) {
          addToast({ type: "info", title: t(GlobalMessages.alert), description: alertMessageContent });
        } else {
          setShowMap(true);
          setTravelDetails(travelDet);
        }

      } catch (error) {
        if (!error.response) addToast({ type: "error", title: t(GlobalMessages.error), description: t(GlobalMessages.connectionNotEstablished) });
        else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
      } finally {
        setLoadingTravelMap(false);
      }
    }

  }, [addToast, vehicle, t]);

  // Generate all map elements configured based in their types (markers, polylines, circles, filter)
  const markers: TravelMarkerOptions[] = useMemo(() => [
    ...generateMarkerOptions(travelDetails, travelAnalyticRecords, vehicle, t),
    ...generateAreaLabelOptions(travelDetails, clientDefaultRadiusMeters, t)
  ], [travelDetails, travelAnalyticRecords, vehicle, clientDefaultRadiusMeters, t]);

  // The route as recorded (positions in time order): drawn as the going/returning lines and played by the replay
  const route = useMemo(() => buildTravelRoute(travelAnalyticRecords), [travelAnalyticRecords]);

  // Route lines plus the ZMRC zones crossed
  const polylines = useMemo(() => [...generatePolylinesOptions(route.points), ...polylinesZMRC], [route, polylinesZMRC]);

  // Speed limit for the replay's speedometer: the travel's, else the vehicle's, read from its register when the
  // vehicle object at hand does not carry it (as in the travel history)
  const [vehicleSpeedLimit, setVehicleSpeedLimit] = useState<number | undefined>();

  useEffect(() => {
    if (!showMap || travelDetails?.alert_maximum_speed || vehicle?.maximum_speed_allowed || !vehicle?.id_vehicle) return undefined;

    let active = true;

    api.get(`vehicles/read/${vehicle.id_vehicle}`).then(({ data }) => {
      const found = Array.isArray(data?.result) ? data.result[0] : data?.result;

      if (active && Number(found?.maximum_speed_allowed) > 0) setVehicleSpeedLimit(Number(found.maximum_speed_allowed));
    }).catch(() => undefined);

    return () => {
      active = false;
    };
  }, [showMap, travelDetails?.alert_maximum_speed, vehicle?.maximum_speed_allowed, vehicle?.id_vehicle]);

  const destinationPoint = useMemo(() => (travelDetails?.destination?.latitude && travelDetails?.destination?.longitude
    ? { lat: Number(travelDetails.destination.latitude), lng: Number(travelDetails.destination.longitude) }
    : undefined), [travelDetails]);

  const circles = useMemo(
    () => generateCirclesOptions(travelDetails, clientDefaultRadiusMeters), [travelDetails, clientDefaultRadiusMeters]
  );
  const polygonPlaces = useMemo(() => generatePolygonsOptions(travelDetails), [travelDetails]);

  // Close modal actions
  const handleClose = useCallback(() => {
    onClose();
    setTimeout(() => { setShowMap(false); }, 1000);
  }, [onClose]);

  const handleOpenVehicleBlockModal = useCallback(() => {

    setOpenModalVehicleBlock(true);

  }, []);

  const handleOpenVehicleMaintenanceModal = useCallback(async () => {

    setOpenModalVehicleMaintenance(true);

  }, []);

  const handleOpenCreateAlertSpeedModal = useCallback(async () => {
    setOpenModalCreateAlertSpeed(true);
  }, []);

  const workHeaderContainer = useMemo(() => {

    let description = t(VehicleAndTravelDetailsMessages.noDescriptionRegistered);

    if (travel && travel.description) description = travel.description;

    if (isMobile) {

      return (
        <div className="block vehicle-travel-details__work-header">
          <div className="icon">
            <ReactSVG src={IconObra} />
          </div>
          <div className="content">
            <div className="title">{t(VehicleAndTravelDetailsMessages.construction)}</div>
            <div className="subtitle">
              { description }
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="block vehicle-travel-details__work-header">
        <div className="box">
          <div className="title">{t(VehicleAndTravelDetailsMessages.construction)}</div>
          <div className="text">
            { description }
          </div>
        </div>
      </div>
    );
  }, [isMobile, travel, t]);

  const statusContainer = useMemo(() => {

    if (!historical) {
      return (<VehicleStatus vehicle={vehicle} historical={historical} />);
    }

    return (<></>);
  }, [vehicle, historical]);

  const travelDataContainer = useMemo(() => {

    if (!$.isEmptyObject(travel)) {

      if (isAggregateOrSiloTruck) return (<AggregateTrucksTravelData travel={travel} vehicle={vehicle} />);

      return (<TravelData travel={travel} vehicle={vehicle} />);
    }

    return (<></>);
  }, [travel, vehicle, isAggregateOrSiloTruck]);

  const alertsContainer = useMemo(() => {

    if (vehicle) {
      return (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography component="div">
              <div className="title">{t(VehicleAndTravelDetailsMessages.alertsTitle)}</div>
              { alerts && (
                <div className="qtd">
                  <Badge
                    badgeContent={alerts.filter((alert) => !alert.confirmation_date).length}
                    color="error"
                  />
                </div>
              ) }
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography component="div">
              <div className="date">
                { !$.isEmptyObject(vehicle)
                && alerts
                && alerts.length > 0 ? (
                    <>
                      <div className="title">{t(GlobalMessages.date)}</div>
                      <div className="text">
                        { utils.formatDateIfHave(
                          alerts[0]?.event?.start_date ?? "",
                          "date"
                        ) }
                      </div>
                    </>
                  ) : (
                    <div className="no-event">{t(VehicleAndTravelDetailsMessages.noAlertsOnTravel)}</div>
                  ) }
              </div>
              <AlertModalList alerts={alerts ?? []} onlyView={onlyView} />
            </Typography>
          </AccordionDetails>
        </Accordion>
      );
    }

    return (<></>);
  }, [vehicle, onlyView, t, alerts]);

  const currentLoadContainer = useMemo(() => {

    if (isAggregateOrSiloTruck && vehicle?.current_travel?.service_order) {
      return (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography component="div">
              <div className="title">{t(VehicleAndTravelDetailsMessages.currentLoad)}</div>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div>
              <Typography component="div" className="current-load-information">
                <div className="current-load-information__icon">
                  { utils.getIconAccordingProductType(vehicle?.current_travel?.service_order?.product) }
                </div>
                <div className="current-load-information__details">
                  <span className="current-load-information__details--title">{ utils.getLabelAccordingProductType(
                    vehicle?.current_travel?.service_order?.product
                  ) }
                  </span>
                  <div style={{ display: "flex", gap: "0.25rem" }}>
                    <p>{ vehicle?.current_travel?.service_order?.quantity }</p>
                    <span style={{ color: "#666666" }}>{ vehicle?.current_travel?.service_order?.unit }</span>
                  </div>
                  <div className="current-load-information__details--observation">
                    { vehicle?.current_travel?.service_order?.observation }
                  </div>
                </div>
              </Typography>
            </div>
          </AccordionDetails>
        </Accordion>
      );
    }

    return (<></>);
  }, [vehicle, isAggregateOrSiloTruck, t]);

  const waterAdditionsContainer = useMemo(() => {

    if (vehicle?.type?.id_vehicle_type !== VehicleTypesID.BETONEIRA) return (<></>);

    // Water reassigned AWAY from this travel (reused in the next one) is shown only as an informational
    // block — it must NOT be counted in this travel's totals (it already counts on the destination travel).
    const ownAdditions = waterAdditions?.filter((wa) => !wa.is_reassigned_away) ?? [];

    const totalWaterAddedPostDischarge = ownAdditions.reduce(
      (acc, curr) => {
        if (curr.is_post_discharge) {
          return acc + curr.water_added;
        }

        return acc;
      }, 0
    );

    const totalWaterAddedInTravel = ownAdditions.reduce(
      (acc, curr) => acc + curr.water_added, 0
    );

    const totalWaterAddedPreDischarge = totalWaterAddedInTravel - totalWaterAddedPostDischarge;

    return (
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography component="div">
            <div className="title">{t(VehicleAndTravelDetailsMessages.waterAdded)} </div>
            {vehicle.alerts && (
              <div className="qtd">
                <Badge
                  badgeContent={ownAdditions.length}
                  color="error"
                />
              </div>
            )}
          </Typography>
        </AccordionSummary>
        <div className="subtitle">
          <div className="bold"> {t(VehicleAndTravelDetailsMessages.waterAdditionPreDischarge)}: &nbsp; </div>
          {isNaN(totalWaterAddedPreDischarge) ? 0 : utils.formatNumberToLocaleString(totalWaterAddedPreDischarge, i18n.language as TLanguages, 2) }L
        </div>
        <div className="subtitle">
          <div className="bold"> {t(VehicleAndTravelDetailsMessages.waterAdditionPostDischarge)}: &nbsp; </div>
          {utils.formatNumberToLocaleString(totalWaterAddedPostDischarge, i18n.language as TLanguages, 2) ?? 0}L
        </div>
        <div className="subtitle">
          <div className="bold"> {t(VehicleAndTravelDetailsMessages.litersAdded)}: &nbsp; </div>
          {utils.formatNumberToLocaleString(totalWaterAddedInTravel, i18n.language as TLanguages, 2) ?? 0}L
        </div>
        <AccordionDetails>
          <Typography component="div">
            <div className="date">
              {!$.isEmptyObject(vehicle)
                && travel?.water_additions?.length > 0 ? (
                  <>
                    <div className="title">{t(GlobalMessages.date)}</div>
                    <div className="text">
                      {utils.formatDateIfHave(
                        travel.water_additions[0].start_date ?? "",
                        "date"
                      )}
                    </div>
                  </>
                ) : (
                  <div className="no-event">{t(VehicleAndTravelDetailsMessages.noWaterAdded)}</div>
                )}
            </div>
            <WaterAdditionModalList waterAdditions={waterAdditions ?? []} />
          </Typography>
        </AccordionDetails>
      </Accordion>
    );

  }, [vehicle, travel, t, waterAdditions]);

  const statesContainer = useMemo(() => {

    if (vehicle) {

      const lastDueDischarge = travel?.last_due_unload_date;
      const dischargeFinishDate = vehicle?.states?.find(
        (state) => state.status.id_vehicle_state_type === VehicleStatesTypesID.DESCARREGANDO
      )?.finish_date;

      const idlePostDischarge = (lastDueDischarge && dischargeFinishDate)
        ? utils.calcDataRange(lastDueDischarge, dischargeFinishDate) as Duration
        : undefined;

      return (<TimeLineVehicleStates vehicle={vehicle} historicalTravel={historical} postUnloadIdleTime={idlePostDischarge} />);
    }

    return (<></>);
  }, [vehicle, historical]);

  /**
   * Search zones
   */
  const readZones = useCallback(async () => {

    function formatToPatch(paths) {
      return [{ paths, class: "zmrc" }];
    }

    try {

      // Get the zones
      const { data } = await api.get("zones/read");

      if (data.status === "success") {

        let coordinatesGeometry = data.result.map((zone: Zones) => {

          if (zone?.geom?.type === "GeometryCollection") {
            return zone?.geom.geometries
              .map((geometries) => geometries["coordinates"]
                .map((arrayPathes) => arrayPathes
                  .map((path) => ({
                    lat: path[1],
                    lng: path[0]
                  })))).flat(1).map((coord) => ({ paths: [coord], class: "zmrc" }));
          }
        }).filter((patches) => patches !== undefined);

        const coordinatesPolygon = formatToPatch(
          data.result.map((zone: Zones) => {
            if (zone?.geom?.type === "Polygon") {
              return zone?.geom?.coordinates.map((arrayPathes) => arrayPathes.map((path) => ({
                lat: path[1],
                lng: path[0]
              })));
            }
          }).filter((patches) => patches !== undefined).flat(1)
        );

        coordinatesGeometry = [...coordinatesGeometry, coordinatesPolygon];

        const coordinatesLines = data.result.map((zone: Zones) => {

          if (zone?.geom?.type === "LineString") {
            return zone?.geom.coordinates.map((path) => ({
              lat: path[1],
              lng: path[0]
            }));
          }
        }).filter((patches) => patches !== undefined).map((coord) => ({ path: [coord] }));

        setPolygons(coordinatesGeometry);

        coordinatesLines.forEach((coord) => {

          // Default options of polylines
          const defaultOptions: PolylineOptions = { strokeOpacity: 0.5, strokeWeight: 3, geodesic: false };

          setPolylinesZMRC((polylinesZMRC) => [...polylinesZMRC, {
            ...defaultOptions,
            "path": coord.path.flat(1),
            "strokeColor": "#FFA500",
            "class": "zmrc"
          }]);
        });

      }

    } catch (error) {

      console.log(error);

    }

  }, [addToast, t]);

  const mapContainer = useMemo(() => {

    if (showMap && markers.length > 0) {
      return (
        <TravelMap
          markers={markers}
          polylines={polylines}
          polygons={polygons}
          polygonPlaces={polygonPlaces}
          circles={circles}
          route={route}
          destination={destinationPoint}
          maxSpeed={travelDetails?.alert_maximum_speed || vehicle?.maximum_speed_allowed || vehicleSpeedLimit || undefined}
          vehicleTypeId={vehicle?.type?.id_vehicle_type}
          hasNote={!!travelDetails?.num_doc}
          mapFilter={mapFilter}
          setMapFilter={setMapFilter}
          details={(
            <>
              {workHeaderContainer}
              {travelDataContainer}
            </>
          )}
          t={t}
        />
      );
    }

    return (
      <div className="vehicle-travel-details__map--static">
        { loadingTravelMap
          ? <CircularProgress />
          : (
            <img
              src={LogoFleet}
              className="vehicle-travel-details__map-icon"
              alt={t(MapMessages.viewLocation)}
              onClick={() => { analyzeTravelDetail(travel); }}
              aria-hidden="true"
            />
          ) }
      </div>
    );
  }, [loadingTravelMap, analyzeTravelDetail, travel, markers, polylines, polygons, polygonPlaces, circles, route, destinationPoint, mapFilter,
    showMap, workHeaderContainer, travelDataContainer, travelDetails, vehicle, vehicleSpeedLimit, t]);

  const mainContainer = useMemo(() => {

    if (isMobile) {
      return (
        <DialogContentText tabIndex={-1} component="div">
          <div className="container">
            { workHeaderContainer }
          </div>
          <div className="vehicle-travel-details__status">
            { statusContainer }
          </div>
          <div className="container">
            { travelDataContainer }
          </div>
          <div className="container">
            <div className="block events">
              { alertsContainer }
            </div>
          </div>
          <div className="container">
            { (vehicle.type.id_vehicle_type === VehicleTypesID.CARRETA_AGREGADOS
                || vehicle.type.id_vehicle_type === VehicleTypesID.CARRETA_SILO)
              && (
                <div className="block currentLoad">
                  { currentLoadContainer }
                </div>
              ) }
          </div>
          <div className="container">
            <div className="block water-additions blue-background-badge">
              { waterAdditionsContainer }
            </div>
          </div>
          <div className="container">
            <div className="block states">
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography component="div">{t(VehicleAndTravelDetailsMessages.travelStatus)}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography component="div">
                    <div className="date">
                      <div className="title">{t(GlobalMessages.date)}</div>
                      <div className="text">
                        { !$.isEmptyObject(vehicle)
                          && utils.formatDateIfHave(
                            vehicle.states[vehicle.states.length - 1]
                              ?.registration_date,
                            "date"
                          ) }
                      </div>
                    </div>
                    { statesContainer }
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </div>
          </div>
          <div className="container">
            <div className="block vehicle-travel-details__map">
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography component="div">{t(VehicleAndTravelDetailsMessages.mapPosition)}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography component="div">
                    { mapContainer }
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </div>
          </div>
        </DialogContentText>
      );
    }

    return (
      <Grid container spacing={2} className="vehicle-travel-details__list">
        <Grid item xs={12} md={12} lg={4} className="vehicle-travel-details__item-grid">
          <div className="container vehicle-travel-details__status">
            <div className="block">
              { statusContainer }
            </div>
          </div>
          <div className="container timeline-states">
            <div className="block">
              { statesContainer }
            </div>
          </div>
        </Grid>
        <Grid item xs={12} md={12} lg={5} className="vehicle-travel-details__item-grid">
          <div className="container info">
            { workHeaderContainer }
            <div className="block vehicle-travel-details__map">
              { mapContainer }
            </div>
            { travelDataContainer }
          </div>
        </Grid>
        <Grid
          item
          xs={12}
          md={12}
          lg={3}
          className="vehicle-travel-details__item-grid vehicle-travel-details__item-grid--alerts"
        >
          <div className="vehicle-travel-details__events">
            { (vehicle.type.id_vehicle_type === VehicleTypesID.CARRETA_AGREGADOS
                || vehicle.type.id_vehicle_type === VehicleTypesID.CARRETA_SILO)
              && (
                <div className="block">
                  { currentLoadContainer }
                </div>
              ) }
            <div className="block">
              { alertsContainer }
            </div>
            <div className="block blue-background-badge">
              { waterAdditionsContainer }
            </div>
          </div>
        </Grid>
      </Grid>
    );
  }, [
    isMobile,
    vehicle,
    statusContainer,
    travelDataContainer,
    alertsContainer,
    waterAdditionsContainer,
    statesContainer,
    mapContainer,
    workHeaderContainer,
    currentLoadContainer,
    t,
    alerts
  ]);

  /**
   * Load alerts from vehicle
   */
  const getAlertFromVehicle = useCallback(async () => {

    setIsLoading(true);

    try {

      const { data } = await api.get(`alerts/read-by-vehicle-id/${vehicle.id_vehicle}`);

      if (data.status === "success") {

        return data.result as Alert[];

      } else {

        addToast({ type: "error", title: t(GlobalMessages.error), description: data.message });

        return [];
      }

    } catch (error) {

      if (!error.response) addToast({ type: "error", title: t(GlobalMessages.error), description: t(GlobalMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });

      return [];

    } finally {
      setIsLoading(false);
    }
  }, [genericVehicle]);

  // Fill maximum_speed_allowed vehicle into alerts
  useEffect(() => {
    const fetchAlerts = async () => {
      let newAlerts = vehicle?.alerts ?? [];

      if (isVehicleAlertSummary(genericVehicle)) {
        newAlerts = await getAlertFromVehicle();
        setVehicle({ ...vehicle, alerts: newAlerts });
      }

      // 'Entrada em local confiável' and 'Abastecimento' are events, not alerts (frontend-only,
      // 10/08/2026) — they stay out of this modal and live only on the alerts-and-events history.
      newAlerts = newAlerts.filter((alert) => !NonAlertEventTypesID.includes(alert?.type?.id_alert_type ?? ""));

      const alerts = newAlerts.map((alert) => ({
        ...alert,
        vehicle: {
          ...vehicle,
          maximum_speed_allowed: vehicle.maximum_speed_allowed ?? alert.vehicle?.maximum_speed_allowed
        }
      }));

      setAlerts(alerts);
    };

    fetchAlerts().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genericVehicle]);

  // Load the client's default construction radius once, so the obra circle matches the radius the
  // worker actually applied. Until it arrives the circle falls back to the system default (300 m).
  useEffect(() => {
    const fetchDefaultRadius = async () => {
      const params = await getParamsObj();
      const radius = Number(params[ParamsNames.DEFAULT_CONSTRUCTION_RADIUS]);

      if (radius) setClientDefaultRadiusMeters(radius);
    };

    fetchDefaultRadius().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // endregion Functions
  return (
    <Dialog
      open={open}
      onClose={() => handleClose()}
      scroll="paper"
      fullWidth
      // On desktop the details take 85% of the screen: the map, its replay and the travel data need the room
      maxWidth={isMobile ? "lg" : false}
      PaperProps={isMobile ? undefined : { style: { width: "85%" } }}
      className={`vehicle-travel-details__dialog ${overAll ? "vehicle-travel-details__dialog--overAll" : ""}`}
    >
      <Loading loading={isLoading} />
      { !isLoading && (
        <Stylesheet isMobile={isMobile} isBlocked={isBlocked}>
          <div className="vehicle-travel-details__header">
            <div className="vehicle-travel-details__title">
              <div
                className="icon"
                style={{ backgroundColor: utils.getVehicleIconColor(vehicle, user, improperDischargeAlerts) }}
              >
                { utils.getVehicleIconAccordingTypeAndAlert(vehicle, improperDischargeAlerts) }
              </div>
              <div className="content">
                <div className="content__title">
                  { vehicle.code } - { vehicle.description } - { isBlocked
                    ? <span style={{ color: "#F00", fontWeight: "bold" }}> {t(TimelineVehicleStatesMessages.blockedVehicle)}</span>
                    : (vehicle.license_plate) }
                </div>
                { vehicle?.current_location && (
                  <div className="content__subtitle">
                    {t(VehicleAndTravelDetailsMessages.allocation).toUpperCase()}:
                    { ` ${vehicle.current_location.address}, ${
                      vehicle.current_location.number || ""
                    }
                      - ${vehicle.current_location.district || ""} | ${
                    vehicle.current_location.county
                  } - ${vehicle.current_location.uf}` }
                  </div>
                ) }
              </div>
            </div>
            <div className="actions">
              { vehicle.current_travel && !travel.alert_maximum_speed && (
                <Button
                  className="create_alert_button"
                  startIcon={<AlertIconGreen width="2rem" />}
                  onClick={() => handleOpenCreateAlertSpeedModal()}
                >
                  {t(TravelAlertFullSpeedMessage.createAlertSpeedTravel)}
                  <br />
                  {t(TravelAlertFullSpeedMessage.speed)}
                </Button>
              )}
              { vehicle.current_travel && travel.alert_maximum_speed && (
                <Button
                  className="show_speed_alert_button"
                  style={{ backgroundColor: vehicle.current_speed && vehicle.current_speed > travel.alert_maximum_speed ? "red" : "green", width: "auto", textTransform: "none" }}
                  startIcon={vehicle.current_speed && vehicle.current_speed > travel.alert_maximum_speed ? <AlertIconRed width="3rem" /> : <AlertIconGreen width="3rem" />}
                  onClick={() => handleOpenCreateAlertSpeedModal()}
                  variant="contained"
                >
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div>
                      <span style={{ fontSize: "larger" }}>{vehicle.current_speed}</span>
                      <PlayArrowIcon />
                      <span style={{ fontSize: "larger" }}>{travel.alert_maximum_speed}</span>
                      km/h
                    </div>
                    <p style={{ color: "white" }}>
                      {vehicle.current_speed && vehicle.current_speed > travel.alert_maximum_speed
                        ? `${t(GlobalMessages.speed)} ${t(GlobalMessages.exceededPT)} ` : `${t(GlobalMessages.speed)} Nominal`}
                    </p>
                  </div>
                </Button>
              )}

              <Button
                className="maintenance_button"
                startIcon={<ManutencaoIcon width="2rem" />}
                onClick={() => handleOpenVehicleMaintenanceModal()}
                variant="contained"
              >
                {t(VehicleMaintenanceMessages.maintenance)}
              </Button>
              { user.allowed_vehicle_block && vehicle.hardwares?.[0]?.block_capable && (
                <div>
                  <Button
                    className="block_button"
                    startIcon={isBlocked
                      ? <UnblockVehicleIcon width="2rem" />
                      : <BlockVehicleIcon width="2rem" />}
                    onClick={() => handleOpenVehicleBlockModal()}
                    variant="contained"
                  >
                    { isBlocked ? t(VehicleAndTravelDetailsMessages.unlockVehicle) : t(VehicleAndTravelDetailsMessages.lockVehicle) }
                  </Button>
                </div>
              ) }
            </div>
          </div>

          <DialogContent
            dividers
            className={`vehicle-travel-details__content vehicle-travel-details__content--${isMobile
              ? "mobile" : "desktop"}`}
          >
            { mainContainer }
          </DialogContent>
          <DialogActions className="vehicle-travel-details__footer">
            <Button disableRipple onClick={() => handleClose()} color="primary">
              {t(GlobalMessages.close)}
            </Button>
          </DialogActions>
          <ModalVehicleBlock
            open={openModalVehicleBlock}
            onClose={() => {
              setOpenModalVehicleBlock(false);
              handleClose();
            }}
            vehicle={vehicle}
            isBlocked={isBlocked}
          />
          <ModalVehicleMaintenance
            open={openModalVehicleMaintenance}
            onClose={() => {
              setOpenModalVehicleMaintenance(false);
              handleClose();
            }}
            vehicle={vehicle}
            isInMaintenance={vehicle.maintenance}
          />

          <ModalSpeedTravel
            open={openModalCreateAlert}
            onClose={() => {
              setOpenModalCreateAlertSpeed(false);
              handleClose();
            }}
            travel={travel}
            vehicle={vehicle}
          />
        </Stylesheet>
      )}
    </Dialog>
  );
};

export {
  ModalVehicleAndTravelDetails
};
